import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const initialProfile = {
  name: "",
  username: "",
  email: "",
  contact: "",
  designation: "",
  department: "",
  bio: "",
};

const initialPassword = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const SuperAdminSettings = () => {
  const token = localStorage.getItem("token");
  const [profileForm, setProfileForm] = useState(initialProfile);
  const [passwordForm, setPasswordForm] = useState(initialPassword);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const authConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/profile`,
        authConfig
      );
      setProfileForm({
        ...initialProfile,
        ...(res.data?.currentUser || {}),
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to load settings profile"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    try {
      setSavingProfile(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/profile`,
        profileForm,
        authConfig
      );
      setProfileForm({
        ...initialProfile,
        ...(res.data?.currentUser || {}),
      });
      toast.success(res.data?.message || "Profile updated successfully");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to update profile"
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    try {
      setSavingPassword(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/change-password`,
        passwordForm,
        authConfig
      );
      setPasswordForm(initialPassword);
      toast.success(res.data?.message || "Password updated successfully");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to update password"
      );
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="mb-1">Super User Settings</h2>
          <p className="text-muted mb-0">
            Manage your platform identity, contact details, and account
            security.
          </p>
        </div>
        <Link to="/superadmin-dashboard" className="btn btn-outline-primary">
          Back To Dashboard
        </Link>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-8">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h4 className="mb-3">Profile Details</h4>
              <form onSubmit={saveProfile}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Full Name</label>
                    <input
                      className="form-control"
                      name="name"
                      value={profileForm.name}
                      onChange={handleProfileChange}
                      disabled={loading || savingProfile}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Username</label>
                    <input
                      className="form-control"
                      name="username"
                      value={profileForm.username}
                      onChange={handleProfileChange}
                      disabled={loading || savingProfile}
                      placeholder="Optional unique handle"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={profileForm.email}
                      onChange={handleProfileChange}
                      disabled={loading || savingProfile}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input
                      className="form-control"
                      name="contact"
                      value={profileForm.contact}
                      onChange={handleProfileChange}
                      disabled={loading || savingProfile}
                      placeholder="Official contact number"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Designation</label>
                    <input
                      className="form-control"
                      name="designation"
                      value={profileForm.designation}
                      onChange={handleProfileChange}
                      disabled={loading || savingProfile}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Department</label>
                    <input
                      className="form-control"
                      name="department"
                      value={profileForm.department}
                      onChange={handleProfileChange}
                      disabled={loading || savingProfile}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Bio / Notes</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      name="bio"
                      value={profileForm.bio}
                      onChange={handleProfileChange}
                      disabled={loading || savingProfile}
                      placeholder="Short internal profile used across the platform"
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || savingProfile}
                  >
                    {savingProfile ? "Saving..." : "Save Profile"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h4 className="mb-3">Password & Security</h4>
              <form onSubmit={changePassword}>
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    className="form-control"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    disabled={savingPassword}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    disabled={savingPassword}
                    minLength={8}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    disabled={savingPassword}
                    minLength={8}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-dark w-100"
                  disabled={savingPassword}
                >
                  {savingPassword ? "Updating..." : "Change Password"}
                </button>
              </form>
            </div>
          </div>

          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h5 className="mb-2">Recommended For Official Platforms</h5>
              <p className="text-muted mb-0">
                Keep your official email, unique username, role title, contact
                number, and a short internal bio up to date so audit trails,
                team collaboration, and identity visibility stay clean.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminSettings;
